import clsx from "clsx"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableContainer from "@material-ui/core/TableContainer"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import Typography from "@material-ui/core/Typography"
import Paper from "@material-ui/core/Paper"
import { Calendar, Clock } from "react-feather"
import { ReportedHourViewModel } from "../../api/reported-hours"
import { Box, Checkbox, Tooltip, useTheme } from "@material-ui/core"
import FullPath from "../../../components/FullPath"
import HourReportActionButton from "../HourReportActionButton"
import { getHourReportStatusLabel, makeHourReportTableHeadCells } from "./table-head-cells"
import { useTranslation } from "react-i18next"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { width: "100%" },
    paper: { width: "100%", marginBottom: theme.spacing(2) },
    tableCell: { "&:not(.lastRow)": { borderBottom: `1px solid ${theme.palette.divider}` } },
    tableHeader: { borderBottom: `1px solid ${theme.palette.divider}`, padding: theme.spacing(2) },
    emptyState: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(6, 2),
      textAlign: "center",
    },
  })
)

const createLink = (entity: { title: string; id: string }, type: "project" | "task") => ({
  label: entity.title,
  href: `/${type}s/${entity.id}`,
  disabled: true,
})

const TaskAncestorLinks = ({ hourReport }: { hourReport: ReportedHourViewModel }) => {
  const taskAncestors = hourReport.task?.ancestors || []
  const projectLink = hourReport.project ? createLink(hourReport.project, "project") : null
  const taskLink = hourReport.task ? createLink(hourReport.task, "task") : null
  const ancestorTaskLinks = taskAncestors.map((ancestor) => createLink(ancestor, "task"))
  const projectOrTaskNumber = hourReport.task?.taskNumber

  if (!projectLink || !taskLink || !projectOrTaskNumber) return null

  return (
    <FullPath
      projectLink={projectLink}
      taskLink={taskLink}
      ancestorTaskLinks={ancestorTaskLinks}
      projectOrTaskNumber={projectOrTaskNumber}
    />
  )
}

const HourReportTableHead = ({ supportsSubmission, statusLabel, labels }: HourReportTableHeadProps) => {
  const classes = useStyles()
  const headCells = makeHourReportTableHeadCells(supportsSubmission, statusLabel, labels)
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            padding={headCell.disablePadding ? "none" : "normal"}
            className={classes.tableCell}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

export default function HourReportTable({
  reports,
  totalReportedHours,
  reportedForName,
  setEditingReportId,
  onReadyForCheckChange,
  onDeleteHourReport,
  supportsSubmission = true,
  supportsChecking,
  supportsApproval,
}: {
  setEditingReportId: (id: string | null) => void
  reports: ReportedHourViewModel[]
  totalReportedHours: { hours: number; minutes: number }
  reportedForName: string
  onReadyForCheckChange: (report: ReportedHourViewModel, checked: boolean) => void
  onDeleteHourReport: (id: string) => void
  supportsSubmission?: boolean
  supportsChecking?: boolean
  supportsApproval?: boolean
}) {
  const theme = useTheme()
  const { t } = useTranslation("hour-report")
  const classes = useStyles()
  const reportCount = reports.length
  const statusLabel = getHourReportStatusLabel({
    supportsChecking,
    supportsApproval,
    labels: {
      readyForCheck: t("status.readyForCheck", { defaultValue: "Ready for check" }),
      readyForApproval: t("status.readyForApproval", { defaultValue: "Ready for approval" }),
    },
  })
  const notReadyStatusLabel = t("status.notReady", {
    defaultValue: "Not {{status}}",
    status: statusLabel.toLowerCase(),
  })

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} variant="outlined" elevation={0}>
        <Box id="tableTitle" className={classes.tableHeader}>
          <Typography variant="body2" color="textSecondary">
            {t("list.total", {
              defaultValue: "Total hours reported: {{hours}}h {{minutes}}m",
              hours: totalReportedHours.hours,
              minutes: totalReportedHours.minutes,
            })}
          </Typography>
        </Box>
        <TableContainer>
          {reportCount === 0 ? (
            <Box className={classes.emptyState}>
              <Clock size={32} color={theme.palette.text.secondary} />
              <Box mt={1.5}>
                <Typography variant="subtitle1">
                  {t("list.emptyTitle", { defaultValue: "Nothing reported yet" })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("list.emptyDescription", {
                    defaultValue: "No hours have been reported for this day for {{name}}",
                    name: reportedForName,
                  })}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Table>
              <HourReportTableHead
                supportsSubmission={supportsSubmission}
                statusLabel={statusLabel}
                labels={{
                  projectTask: t("list.projectTask", { defaultValue: "Project/Task" }),
                  hours: t("list.hours", { defaultValue: "Hours" }),
                  description: t("list.description", { defaultValue: "Description" }),
                  actions: t("list.actions", { defaultValue: "" }),
                }}
              />
              <TableBody>
                {reports.map((report, index) => {
                  const labelId = `hour-report-${report.id}`
                  const isLastRow = index === reportCount - 1

                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={report.id}>
                      <TableCell
                        className={clsx(classes.tableCell, isLastRow ? "lastRow" : undefined)}
                        component="th"
                        id={labelId}
                        scope="report"
                      >
                        <TaskAncestorLinks hourReport={report} />
                        <Typography>
                          {report.task?.title ||
                            report.project?.displayTitle ||
                            t("list.notSelected", { defaultValue: "Not selected" })}
                        </Typography>
                      </TableCell>
                      <TableCell className={clsx(classes.tableCell, isLastRow ? "lastRow" : undefined)}>
                        <Box>
                          <Typography variant="body2">
                            {report.durationInHoursAndMinutes.hours}h {report.durationInHoursAndMinutes.minutes}m
                          </Typography>
                          {!report.isReportedManually ? (
                            <Box mt={0.5} style={{ display: "flex", alignItems: "center" }}>
                              {report.isReportedByInterval ? (
                                <Calendar size={10} style={{ marginRight: 4, color: theme.palette.text.secondary }} />
                              ) : (
                                <Clock size={10} style={{ marginRight: 4, color: theme.palette.text.secondary }} />
                              )}
                              <Typography variant="caption" color="textSecondary">
                                {report.startTime}-{report.endTime}
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      </TableCell>
                      {supportsSubmission ? (
                        <TableCell className={clsx(classes.tableCell, isLastRow ? "lastRow" : undefined)}>
                          <Tooltip title={report.isReadyForCheck ? statusLabel : notReadyStatusLabel}>
                            <span>
                              <Checkbox
                                checked={report.isReadyForCheck}
                                disabled={!report.isReadyForCheckEnabled}
                                color="primary"
                                size="small"
                                inputProps={{
                                  "aria-label": report.isReadyForCheck ? statusLabel : notReadyStatusLabel,
                                }}
                                onChange={(_, checked) => onReadyForCheckChange(report, checked)}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      ) : null}
                      <TableCell className={clsx(classes.tableCell, isLastRow ? "lastRow" : undefined)}>
                        {report.description || "-"}
                      </TableCell>
                      <TableCell className={clsx(classes.tableCell, isLastRow ? "lastRow" : undefined)}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 4 }}>
                          <HourReportActionButton
                            icon="edit"
                            color="primary"
                            title={t("actions.editReport", { defaultValue: "Edit hour report" })}
                            aria-label={t("actions.editReport", { defaultValue: "Edit hour report" })}
                            onClick={(e) => {
                              e.stopPropagation()
                              setEditingReportId(report.id)
                            }}
                          />
                          <HourReportActionButton
                            icon="delete"
                            color="delete"
                            title={t("actions.deleteReport", { defaultValue: "Delete hour report" })}
                            aria-label={t("actions.deleteReport", { defaultValue: "Delete hour report" })}
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteHourReport(report.id)
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
    </div>
  )
}

type HourReportTableHeadProps = {
  supportsSubmission: boolean
  statusLabel: string
  labels: { projectTask: string; hours: string; description: string; actions: string }
}
